/**
 * Entry point.
 *
 * ORDER IN THIS FILE IS LOAD-BEARING. Two things must happen before React
 * Router is constructed, and both are the first statements below:
 *
 *  1. `captureLaunchParams()` reads `window.location.hash`. Telegram delivers
 *     a Mini App's signed launch data in the hash, and `createBrowserRouter`
 *     owns the URL from the moment it exists.
 *  2. `startThemeSync()` puts `.dark` / `data-theme` on `<html>` before the
 *     first paint. Doing it in an effect renders one frame in the wrong theme.
 */

import { lazy, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { Toast } from '@heroui/react';
import { captureLaunchParams } from './telegram/launch-params.ts';
import { resolveColorScheme, startThemeSync } from './telegram/theme.ts';
import { initWebApp } from './telegram/webapp.ts';
import { AuthProvider } from './auth/AuthProvider.tsx';
import { RequireAdmin, RequireMember } from './components/guards.tsx';

import { LoginPage } from './pages/LoginPage.tsx';
import { NotAMemberPage } from './pages/NotAMemberPage.tsx';
import { NotFoundPage } from './pages/NotFoundPage.tsx';
import { DashboardPage } from './pages/DashboardPage.tsx';
import { MyCharacterPage } from './pages/MyCharacterPage.tsx';
import { SchedulePage } from './pages/SchedulePage.tsx';
import './styles.css';

/*
 * ⚠️ EVERY ADMIN PAGE IS LAZY, AND THAT IS WHAT PAYS FOR THE CHART LIBRARY.
 *
 * The bundle was one 824 kB chunk with no splitting, and Расход ИИ — a page
 * only an admin opens, and rarely — now imports recharts. Loading that on the
 * phone of every member who opens Главная would be the wrong trade entirely.
 *
 * The boundary is `RequireAdmin`, which already renders an <Outlet/> for the
 * whole subtree, so ONE <Suspense> covers all eight pages and the member
 * bundle loses the admin tables as well as the charts.
 */
const AdminHomePage = lazy(() =>
  import('./pages/admin/AdminHomePage.tsx').then((m) => ({ default: m.AdminHomePage })),
);
const AttributesPage = lazy(() =>
  import('./pages/admin/AttributesPage.tsx').then((m) => ({ default: m.AttributesPage })),
);
const PeoplePage = lazy(() =>
  import('./pages/admin/PeoplePage.tsx').then((m) => ({ default: m.PeoplePage })),
);
const KnowledgePage = lazy(() =>
  import('./pages/admin/KnowledgePage.tsx').then((m) => ({ default: m.KnowledgePage })),
);
const AdminSchedulePage = lazy(() =>
  import('./pages/admin/AdminSchedulePage.tsx').then((m) => ({ default: m.AdminSchedulePage })),
);
const AttendancePage = lazy(() =>
  import('./pages/admin/AttendancePage.tsx').then((m) => ({ default: m.AttendancePage })),
);
const AiUsagePage = lazy(() =>
  import('./pages/admin/AiUsagePage.tsx').then((m) => ({ default: m.AiUsagePage })),
);
const LootRotationPage = lazy(() =>
  import('./pages/admin/LootRotationPage.tsx').then((m) => ({ default: m.LootRotationPage })),
);

captureLaunchParams();
startThemeSync();

const router = createBrowserRouter([
  // Public.
  { path: '/login', element: <LoginPage /> },
  { path: '/not-a-member', element: <NotAMemberPage /> },

  // Member-only. RequireMember also supplies the signed-in chrome.
  {
    element: <RequireMember />,
    children: [
      { path: '/', element: <DashboardPage /> },
      { path: '/character', element: <MyCharacterPage /> },
      { path: '/schedule', element: <SchedulePage /> },

      // Admin-only, nested so it inherits the member guard too (DECISION-1).
      {
        element: <RequireAdmin />,
        children: [
          { path: '/admin', element: <AdminHomePage /> },
          { path: '/admin/attributes', element: <AttributesPage /> },
          { path: '/admin/people', element: <PeoplePage /> },
          { path: '/admin/knowledge', element: <KnowledgePage /> },
          { path: '/admin/schedule', element: <AdminSchedulePage /> },
          { path: '/admin/attendance', element: <AttendancePage /> },
          { path: '/admin/ai-usage', element: <AiUsagePage /> },
          { path: '/admin/loot', element: <LootRotationPage /> },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element in index.html');

createRoot(container).render(
  <StrictMode>
    <AuthProvider>
      <RouterProvider router={router} />
      {/*
        ⚠️ ONE TOAST REGION FOR THE WHOLE APP, AND IT IS A SIBLING OF THE
        ROUTER, NOT A CHILD OF IT. `notify()` writes into a module-level queue
        (HeroUI's `toastQueue`), so this is the single consumer of it — mounting
        a second one inside a layout would render every toast twice, and
        mounting it inside a route would unmount the message on the navigation
        that half of them are reporting.

        `placement="bottom"`: the top of a phone is the sticky title bar, and on
        a 390px screen a toast landing there covers the name of the page it is
        talking about. The bottom is where the tab bar is — see the
        `.toast-region--bottom` offset in `styles.css`, which lifts the region
        clear of it.

        It renders nothing at all until something is queued.

        ⚠️ AND IT IS NOT `lazy()`, THOUGH IT IS BIG ENOUGH TO TEMPT YOU.
        Measured: the render layer is 6.42 kB gzip of the member bundle (235.32
        → 241.74), which is the same order as the admin split that DECISION-35
        thought worth making. It stays eager anyway, because `<Suspense>` does
        not catch a rejected import (see `ChunkErrorBoundary`) and this one sits
        at the app root with no boundary that could sensibly render in its
        place: the failure mode of a split would be every success and every
        error message in the app silently going missing, with nothing on screen
        saying so. 6 kB is not worth buying that.
      */}
      <Toast.Provider placement="bottom" />
    </AuthProvider>
  </StrictMode>,
);

/**
 * Tell Telegram the app is up — AFTER `render`, and that ordering is the point.
 *
 * `ready()` is what dismisses the client's own loading placeholder. Calling it
 * earlier replaces a spinner with an empty white app for however long the first
 * paint takes; calling it not at all leaves the placeholder over a working app
 * on some clients. `expand()` and `disableVerticalSwipes()` ride along because
 * they are the same "we are a real app now" moment.
 *
 * Outside Telegram every one of these is a no-op.
 */
initWebApp(resolveColorScheme());
